/** @file Inverse bind pose per weighted bone of a skinned mesh; missing or singular ones are VALIDATION. */
import { throwError } from '@pose2d/format/throwError';
import type { MeshAtt } from '@pose2d/format/types';
import { affine } from '@pose2d/math/affine';
import type { Affine } from '@pose2d/math/types';

/** Determinant of the linear part of `[a, b, c, d, tx, ty]`. */
const det = (m: readonly number[]): number => (m[0] ?? 0) * (m[3] ?? 0) - (m[1] ?? 0) * (m[2] ?? 0);

/** bone id -> inverse(bindPose[bone]); empty for a rigid mesh. `path` points at the mesh's geometry. */
export function inverseBindPoses(mesh: MeshAtt, path: string): Map<string, Affine> {
  const out = new Map<string, Affine>();
  const g = mesh.geometry;
  if (g.kind === 'rigid') return out;
  for (const [bone] of g.weights.flat()) {
    if (out.has(bone)) continue;
    const b = g.bindPose[bone];
    if (b === undefined) throwError('VALIDATION', undefined, { path: `${path}.bindPose`, hint: `mesh "${mesh.name}": weighted bone ${bone} has no bindPose entry` });
    if (!Number.isFinite(1 / det(b))) throwError('VALIDATION', undefined, { path: `${path}.bindPose.${bone}`, hint: `mesh "${mesh.name}": the bind pose of ${bone} is singular` });
    out.set(bone, affine.invert(new Float64Array(6), b));
  }
  return out;
}
